import {
  LIMIT_KEYS,
  PAYMENT_METHODS,
  ROLES_BY_SCOPE,
  SUBSCRIPTION_STATUSES,
  TENANT_STATUSES,
  type AdminUser,
  type AuditEntry,
  type Client,
  type Limits,
  type Payment,
  type Plan,
  type Role,
  type ScopeType,
  type Subscription,
  type SubscriptionDetail,
  type Tenant,
} from '@platform/contracts';
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  IconBadge,
  type IconBadgeProps,
  Label,
  Monogram,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
} from '@platform/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  Briefcase,
  Building2,
  Check,
  CheckCircle2,
  CircleSlash,
  CreditCard,
  Eye,
  FileText,
  KeyRound,
  Landmark,
  Layers,
  ListChecks,
  LogOut,
  Banknote,
  Pencil,
  PauseCircle,
  Plus,
  Receipt,
  Repeat,
  ScrollText,
  ShieldCheck,
  ShieldOff,
  TrendingUp,
  UserCheck,
  UserX,
  UsersRound,
  Wallet,
  Wand2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { type SubmitEvent, type ReactNode, useId, useState } from 'react';
import { toast } from 'sonner';

import { Field, FormError, fd } from '../components/form';
import { ImpersonateButton } from '../components/impersonation';
import { Guard, PageHeader } from '../components/page';
import { ConfirmDialog, RowActions } from '../components/row-actions';
import { SUB_STATUS, StatusBadge, SubStatusBadge } from '../components/status-badges';
import { api, errorMessage } from '../lib/api';
import { useTenantLogo } from '../lib/console';
import { fmtDate, fmtPeriod, ROLE_LABELS } from '../lib/format';
import { FEATURE_LABELS, LIMIT_DEFS, featureLabel, limitLines, readLimits } from '../lib/limits';

// ------------------------------------------------------------------ helpers

const SECTIONS: readonly { to: string; label: string; icon: LucideIcon }[] = [
  { to: '/superadmin/clientes', label: 'Clientes', icon: Briefcase },
  { to: '/superadmin/empresas', label: 'Empresas', icon: Building2 },
  { to: '/superadmin/nueva-empresa', label: 'Nueva empresa (wizard)', icon: Wand2 },
  { to: '/superadmin/usuarios', label: 'Usuarios', icon: UsersRound },
  { to: '/superadmin/planes', label: 'Planes', icon: Layers },
  { to: '/superadmin/suscripciones', label: 'Suscripciones', icon: Repeat },
  { to: '/superadmin/facturas', label: 'Facturas', icon: FileText },
  { to: '/superadmin/pagos', label: 'Pagos', icon: Wallet },
  { to: '/superadmin/margenes', label: 'Márgenes', icon: TrendingUp },
  { to: '/superadmin/facturacion', label: 'Datos de facturación', icon: Landmark },
  { to: '/superadmin/auditoria', label: 'Auditoría', icon: ScrollText },
];

export function SuperadminLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <Guard requires="superadmin">
      <PageHeader
        title="Administración"
        description="Clientes, empresas, usuarios y cobro manual de toda la plataforma."
      />
      <nav
        aria-label="Secciones de superadmin"
        className="-mx-1 mb-6 flex gap-1 overflow-x-auto px-1 pb-1 scrollbar-none md:flex-wrap md:overflow-visible"
      >
        {SECTIONS.map((s) => {
          const active = pathname.startsWith(s.to);
          return (
            <Link
              key={s.to}
              to={s.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'border-primary bg-primary font-medium text-primary-foreground shadow-card'
                  : 'border-transparent text-muted-foreground hover:border-border hover:bg-card hover:text-foreground',
              )}
            >
              <s.icon className="size-4" aria-hidden />
              {s.label}
            </Link>
          );
        })}
      </nav>
      <Outlet />
    </Guard>
  );
}

interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
}

/** Tabla de administración: carga con esqueleto, vacío ilustrado y filas con tipografía de datos. */
export function DataTable<T extends { id: string }>({
  rows,
  columns,
  empty,
  emptyIcon,
}: {
  rows: T[] | undefined;
  columns: Column<T>[];
  empty: string;
  emptyIcon?: ReactNode;
}) {
  if (!rows) {
    return (
      <div aria-busy="true" aria-label="Cargando" className="flex flex-col gap-2">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return <EmptyState icon={emptyIcon ?? <Layers />} title={empty} />;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((c, i) => (
            <TableHead key={c.header || String(i)} className={c.className}>
              {c.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            {columns.map((c, i) => (
              <TableCell key={c.header || String(i)} className={c.className}>
                {c.cell(r)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Encabezado de una sección de administración: qué es + la acción de crear. */
function SectionBar({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <IconBadge tone="navy">
          <Icon />
        </IconBadge>
        <div>
          <h2 className="text-lg leading-tight font-semibold">{title}</h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </div>
      {action}
    </div>
  );
}

/**
 * Dialog con un form; `onSubmit` devuelve la promesa de la mutación y cierra si sale bien. Con
 * `open`/`onOpenChange` se controla desde afuera (un menú de acciones) y no dibuja disparador.
 */
export function FormDialog({
  trigger,
  title,
  description,
  submitLabel = 'Guardar',
  wide = false,
  open: controlled,
  onOpenChange,
  onSubmit,
  children,
}: {
  trigger?: ReactNode;
  title: string;
  description?: ReactNode;
  submitLabel?: string;
  wide?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit: (form: FormData) => Promise<unknown>;
  children: ReactNode;
}) {
  const [own, setOwn] = useState(false);
  const open = controlled ?? own;
  const setOpen = onOpenChange ?? setOwn;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    let pending: Promise<unknown>;
    try {
      pending = onSubmit(new FormData(e.currentTarget));
    } catch (err) {
      pending = Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
    pending
      .then(() => {
        setOpen(false);
      })
      .catch((err: unknown) => {
        setError(errorMessage(err));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setError(null);
        setOpen(v);
      }}
    >
      {trigger && controlled === undefined ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : null}
      <DialogContent className={wide ? 'max-w-2xl' : undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {children}
          <FormError message={error} />
          <DialogFooter>
            <Button type="submit" disabled={busy}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** <select> nativo con label (accesible y simple para formularios de administración). */
export function NativeSelect({
  label,
  name,
  options,
  defaultValue,
}: {
  label: string;
  name: string;
  options: readonly { value: string; label: string }[];
  defaultValue?: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        name={name}
        defaultValue={defaultValue}
        className="h-9 rounded-md border border-input bg-card px-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function JsonArea({
  label,
  name,
  defaultValue,
}: {
  label: string;
  name: string;
  defaultValue: unknown;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        name={name}
        defaultValue={JSON.stringify(defaultValue, null, 2)}
        className="font-mono text-xs"
      />
    </div>
  );
}

export function parseJson(raw: string, what: string): unknown {
  try {
    return raw.trim() ? JSON.parse(raw) : {};
  } catch {
    throw new Error(`${what}: JSON inválido`);
  }
}

export const money = (cents: number, currency: string) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency, useGrouping: 'always' }).format(
    cents / 100,
  );
/** Precio de lista: sin céntimos cuando es redondo ("699 €", "29,90 €"). */
const price = (cents: number, currency: string) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    useGrouping: 'always',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
const dateTime = (iso: string) =>
  new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );

export function useAdminList<T>(key: string, path: string) {
  return useQuery({ queryKey: ['admin', key], queryFn: () => api<T[]>(path) });
}

export function useAdminMutation(keys: string[], success: string) {
  const qc = useQueryClient();
  return (fn: () => Promise<unknown>) =>
    fn().then(async (r) => {
      toast.success(success);
      await Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: ['admin', k] })));
      return r;
    });
}

/** Nombre + monograma (cliente, empresa, persona) en una celda. */
function Who({
  name,
  sub,
  colorKey,
  src,
}: {
  name: string;
  sub?: ReactNode;
  colorKey?: string;
  src?: string | null;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Monogram name={name} {...(colorKey ? { colorKey } : {})} src={src} size="sm" />
      <div className="min-w-0">
        <p className="truncate font-medium">{name}</p>
        {sub ? <div className="truncate text-xs text-muted-foreground">{sub}</div> : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Clientes

export function ClientsSection() {
  const clients = useAdminList<Client>('clients', '/api/admin/clients');
  const run = useAdminMutation(['clients'], 'Cliente guardado');
  return (
    <div className="flex flex-col gap-4">
      <SectionBar
        icon={Briefcase}
        title="Clientes"
        description="Quién paga: cada cliente agrupa una o más empresas."
        action={
          <FormDialog
            title="Nuevo cliente"
            trigger={
              <Button>
                <Plus /> Nuevo cliente
              </Button>
            }
            onSubmit={(f) =>
              run(() =>
                api('/api/admin/clients', {
                  method: 'POST',
                  body: { name: fd(f, 'name'), slug: fd(f, 'slug') },
                }),
              )
            }
          >
            <Field label="Nombre" name="name" required />
            <Field
              label="Slug"
              name="slug"
              pattern="[a-z0-9][a-z0-9-]*"
              hint="minúsculas, números y guiones"
              required
            />
          </FormDialog>
        }
      />
      <DataTable
        rows={clients.data}
        empty="Sin clientes."
        emptyIcon={<Briefcase />}
        columns={[
          {
            header: 'Cliente',
            cell: (c) => <Who name={c.name} colorKey={c.slug} sub={<code>{c.slug}</code>} />,
          },
          { header: 'Alta', cell: (c) => fmtDate(c.createdAt) },
          {
            header: '2FA obligatorio',
            cell: (c) => (
              <Switch
                aria-label={`Exigir verificación en dos pasos a todo el equipo de ${c.name}`}
                checked={c.requireMfa}
                onCheckedChange={(requireMfa) => {
                  run(() =>
                    api(`/api/admin/clients/${c.id}`, {
                      method: 'PATCH',
                      body: { requireMfa },
                    }),
                  ).catch((err: unknown) => toast.error(errorMessage(err)));
                }}
              />
            ),
          },
          {
            header: '',
            className: 'w-28 text-right',
            cell: (c) => (
              <FormDialog
                title={`Editar ${c.name}`}
                trigger={
                  <Button variant="outline" size="sm">
                    <Pencil /> Editar
                  </Button>
                }
                onSubmit={(f) =>
                  run(() =>
                    api(`/api/admin/clients/${c.id}`, {
                      method: 'PATCH',
                      body: { name: fd(f, 'name'), slug: fd(f, 'slug') },
                    }),
                  )
                }
              >
                <Field label="Nombre" name="name" defaultValue={c.name} required />
                <Field label="Slug" name="slug" defaultValue={c.slug} required />
              </FormDialog>
            ),
          },
        ]}
      />
    </div>
  );
}

// ------------------------------------------------------------------ Empresas

const STATUS_LABEL: Record<(typeof TENANT_STATUSES)[number], string> = {
  onboarding: 'Onboarding',
  active: 'Activa',
  paused: 'Pausada',
  archived: 'Archivada',
};

const TENANT_STATUS_STYLE: Record<
  (typeof TENANT_STATUSES)[number],
  { tone: NonNullable<BadgeProps['variant']>; icon: LucideIcon }
> = {
  onboarding: { tone: 'default', icon: Wand2 },
  active: { tone: 'success', icon: CheckCircle2 },
  paused: { tone: 'warning', icon: PauseCircle },
  archived: { tone: 'secondary', icon: Archive },
};

function FeatureFlagsEditor({ tenant }: { tenant: Tenant }) {
  const run = useAdminMutation(['tenants'], 'Flags actualizados');
  const flags = {
    inbound_bot: false,
    sheet_export: false,
    client_prompt_edit: false,
    gmail_dossier: false,
    ...tenant.featureFlags,
  };
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {Object.entries(flags).map(([key, on]) => (
        <label key={key} className="flex items-center gap-1.5 text-xs">
          <Switch
            checked={on}
            onCheckedChange={(v) => {
              void run(() =>
                api(`/api/admin/tenants/${tenant.id}`, {
                  method: 'PATCH',
                  body: { featureFlags: { ...flags, [key]: v } },
                }),
              ).catch((err: unknown) => toast.error(errorMessage(err)));
            }}
            aria-label={key}
          />
          {key}
        </label>
      ))}
    </div>
  );
}

function TenantEditDialog({
  tenant,
  run,
  open,
  onOpenChange,
}: {
  tenant: Tenant;
  run: (fn: () => Promise<unknown>) => Promise<unknown>;
  open?: boolean;
  onOpenChange?: (v: boolean) => void;
}) {
  return (
    <FormDialog
      title={`Editar ${tenant.name}`}
      trigger={
        <Button variant="outline" size="sm">
          <Pencil /> Editar
        </Button>
      }
      {...(open === undefined ? {} : { open, onOpenChange })}
      onSubmit={(f) =>
        run(() =>
          api(`/api/admin/tenants/${tenant.id}`, {
            method: 'PATCH',
            body: {
              name: fd(f, 'name'),
              status: fd(f, 'status'),
              timezone: fd(f, 'timezone'),
              engineLocation: fd(f, 'engineLocation'),
            },
          }),
        )
      }
    >
      <Field label="Nombre" name="name" defaultValue={tenant.name} required />
      <NativeSelect
        label="Estado"
        name="status"
        defaultValue={tenant.status}
        options={TENANT_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
      />
      <Field label="Zona horaria" name="timezone" defaultValue={tenant.timezone} required />
      <Field
        label="Motor"
        name="engineLocation"
        defaultValue={tenant.engineLocation}
        hint="stack:<nombre> o shared"
        required
      />
    </FormDialog>
  );
}

export function TenantsSection() {
  const tenantLogo = useTenantLogo();
  const clients = useAdminList<Client>('clients', '/api/admin/clients');
  const [clientId, setClientId] = useState('');
  const tenants = useQuery({
    queryKey: ['admin', 'tenants', clientId],
    queryFn: () => api<Tenant[]>(`/api/admin/tenants${clientId ? `?clientId=${clientId}` : ''}`),
  });
  const run = useAdminMutation(['tenants'], 'Empresa guardada');
  const clientOptions = (clients.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  const filterId = useId();
  return (
    <div className="flex flex-col gap-4">
      <SectionBar
        icon={Building2}
        title="Empresas"
        description="Cada empresa es un negocio con su campaña, su motor y sus funciones."
        action={
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={filterId} className="text-xs">
                Cliente
              </Label>
              <select
                id={filterId}
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                }}
                className="h-9 rounded-md border border-input bg-card px-2 text-sm shadow-xs"
              >
                <option value="">Todos</option>
                {clientOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <FormDialog
              title="Nueva empresa"
              trigger={
                <Button>
                  <Plus /> Nueva empresa
                </Button>
              }
              onSubmit={(f) =>
                run(() =>
                  api('/api/admin/tenants', {
                    method: 'POST',
                    body: {
                      clientId: fd(f, 'clientId'),
                      name: fd(f, 'name'),
                      slug: fd(f, 'slug'),
                      timezone: fd(f, 'timezone'),
                    },
                  }),
                )
              }
            >
              <NativeSelect label="Cliente" name="clientId" options={clientOptions} />
              <Field label="Nombre" name="name" required />
              <Field label="Slug" name="slug" pattern="[a-z0-9][a-z0-9-]*" required />
              <Field label="Zona horaria" name="timezone" defaultValue="Europe/Madrid" required />
            </FormDialog>
          </div>
        }
      />
      <DataTable
        rows={tenants.data}
        empty="Sin empresas."
        emptyIcon={<Building2 />}
        columns={[
          {
            header: 'Empresa',
            cell: (t) => (
              <Who
                name={t.name}
                colorKey={t.slug}
                src={tenantLogo(t.slug)}
                sub={<code>{t.slug}</code>}
              />
            ),
          },
          {
            header: 'Cliente',
            cell: (t) => clients.data?.find((c) => c.id === t.clientId)?.name ?? '—',
          },
          {
            header: 'Estado',
            cell: (t) => (
              <StatusBadge
                label={STATUS_LABEL[t.status]}
                tone={TENANT_STATUS_STYLE[t.status].tone}
                icon={TENANT_STATUS_STYLE[t.status].icon}
              />
            ),
          },
          {
            header: 'Motor',
            cell: (t) => (
              <code className="rounded-md bg-muted px-1.5 py-0.5 text-xs">{t.engineLocation}</code>
            ),
          },
          { header: 'Feature flags', cell: (t) => <FeatureFlagsEditor tenant={t} /> },
          {
            header: '',
            className: 'w-44 text-right',
            cell: (t) => (
              <div className="flex items-center justify-end gap-1.5">
                <Button asChild variant="ghost" size="sm">
                  <a href={`/superadmin/nueva-empresa?tenant=${t.id}`}>
                    <ListChecks /> Checklist
                  </a>
                </Button>
                <TenantEditDialog tenant={t} run={run} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

// ------------------------------------------------------------------ Usuarios

function MembershipDialog({
  user,
  clients,
  tenants,
}: {
  user: AdminUser;
  clients: Client[];
  tenants: Tenant[];
}) {
  const run = useAdminMutation(['users'], 'Membresía agregada');
  const [scopeType, setScopeType] = useState<ScopeType>('tenant');
  const agencies = useAdminList<{ id: string; name: string }>('agencies', '/api/admin/agencies');
  const scopeOptions =
    scopeType === 'agency'
      ? (agencies.data ?? []).map((a) => ({ value: a.id, label: a.name }))
      : scopeType === 'client'
        ? clients.map((c) => ({ value: c.id, label: c.name }))
        : tenants.map((t) => ({ value: t.id, label: t.name }));
  const typeId = useId();
  return (
    <FormDialog
      title={`Membresía para ${user.email}`}
      submitLabel="Agregar membresía"
      trigger={
        <Button variant="outline" size="sm">
          <Plus /> Membresía
        </Button>
      }
      onSubmit={(f) =>
        run(() =>
          api(`/api/admin/users/${user.id}/memberships`, {
            method: 'POST',
            body: { scopeType, scopeId: fd(f, 'scopeId'), role: fd(f, 'role') },
          }),
        )
      }
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={typeId}>Alcance</Label>
        <select
          id={typeId}
          value={scopeType}
          onChange={(e) => {
            setScopeType(e.target.value as ScopeType);
          }}
          className="h-9 rounded-md border border-input bg-card px-2 text-sm shadow-xs"
        >
          <option value="agency">Agencia</option>
          <option value="client">Cliente</option>
          <option value="tenant">Empresa</option>
        </select>
      </div>
      <NativeSelect key={`s-${scopeType}`} label="Dónde" name="scopeId" options={scopeOptions} />
      <NativeSelect
        key={`r-${scopeType}`}
        label="Rol"
        name="role"
        options={ROLES_BY_SCOPE[scopeType].map((r: Role) => ({
          value: r,
          label: ROLE_LABELS[r] ?? r,
        }))}
      />
    </FormDialog>
  );
}

function UserActions({
  user,
  clients,
  tenants,
  act,
}: {
  user: AdminUser;
  clients: Client[];
  tenants: Tenant[];
  act: (fn: () => Promise<unknown>) => Promise<unknown>;
}) {
  const [confirm, setConfirm] = useState<'disable' | 'reset' | null>(null);
  const [viewAs, setViewAs] = useState(false);
  const active = user.status === 'active';
  return (
    <>
      <RowActions
        label={user.email}
        primary={<MembershipDialog user={user} clients={clients} tenants={tenants} />}
        items={[
          ...(!user.isSuperadmin && active
            ? [
                {
                  label: 'Ver como esta persona',
                  icon: Eye,
                  onSelect: () => {
                    setViewAs(true);
                  },
                },
              ]
            : []),
          {
            label: 'Cerrar sus sesiones',
            icon: LogOut,
            onSelect: () => {
              void act(() =>
                api(`/api/admin/users/${user.id}/revoke-sessions`, { method: 'POST' }),
              );
            },
          },
          {
            label: 'Resetear 2FA',
            icon: KeyRound,
            separatorBefore: true,
            destructive: true,
            onSelect: () => {
              setConfirm('reset');
            },
          },
          active
            ? {
                label: 'Deshabilitar usuario',
                icon: UserX,
                destructive: true,
                onSelect: () => {
                  setConfirm('disable');
                },
              }
            : {
                label: 'Habilitar usuario',
                icon: UserCheck,
                onSelect: () => {
                  void act(() =>
                    api(`/api/admin/users/${user.id}`, {
                      method: 'PATCH',
                      body: { status: 'active' },
                    }),
                  );
                },
              },
        ]}
      />
      {viewAs ? <ImpersonateButton user={user} open={viewAs} onOpenChange={setViewAs} /> : null}
      <ConfirmDialog
        open={confirm === 'reset'}
        onOpenChange={(v) => {
          if (!v) setConfirm(null);
        }}
        title={`¿Resetear el 2FA de ${user.name}?`}
        description="Va a tener que volver a configurar la verificación en dos pasos en su próximo inicio de sesión."
        confirmLabel="Resetear 2FA"
        onConfirm={() =>
          act(() => api(`/api/admin/users/${user.id}/reset-2fa`, { method: 'POST' }))
        }
      />
      <ConfirmDialog
        open={confirm === 'disable'}
        onOpenChange={(v) => {
          if (!v) setConfirm(null);
        }}
        title={`¿Deshabilitar a ${user.name}?`}
        description="No va a poder entrar al panel hasta que lo habilites de nuevo. No se borra nada."
        confirmLabel="Deshabilitar"
        onConfirm={() =>
          act(() =>
            api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: { status: 'disabled' } }),
          )
        }
      />
    </>
  );
}

export function UsersSection() {
  const users = useAdminList<AdminUser>('users', '/api/admin/users');
  const clients = useAdminList<Client>('clients', '/api/admin/clients');
  const tenants = useQuery({
    queryKey: ['admin', 'tenants', ''],
    queryFn: () => api<Tenant[]>('/api/admin/tenants'),
  });
  const run = useAdminMutation(['users'], 'Usuario actualizado');
  const scopeName = (type: ScopeType, id: string) =>
    type === 'agency'
      ? 'agencia'
      : type === 'client'
        ? (clients.data?.find((c) => c.id === id)?.name ?? 'cliente')
        : (tenants.data?.find((t) => t.id === id)?.name ?? 'empresa');
  const act = (fn: () => Promise<unknown>) =>
    run(fn).catch((err: unknown) => {
      toast.error(errorMessage(err));
      throw err;
    });
  return (
    <div className="flex flex-col gap-4">
      <SectionBar
        icon={UsersRound}
        title="Usuarios"
        description="Personas con acceso al panel, sus roles y su seguridad."
      />
      <DataTable
        rows={users.data}
        empty="Sin usuarios."
        emptyIcon={<UsersRound />}
        columns={[
          {
            header: 'Usuario',
            cell: (u) => (
              <Who
                name={u.name}
                colorKey={u.email}
                sub={
                  <span className="inline-flex items-center gap-1.5">
                    {u.email}
                    {u.isSuperadmin ? (
                      <Badge className="py-0 text-[0.65rem]">superadmin</Badge>
                    ) : null}
                  </span>
                }
              />
            ),
          },
          {
            header: 'Membresías',
            cell: (u) => (
              <div className="flex max-w-md flex-wrap gap-1">
                {u.memberships.length === 0 ? (
                  <span className="text-xs text-muted-foreground">Sin membresías</span>
                ) : null}
                {u.memberships.map((m) => (
                  <Badge key={m.id} variant="outline" className="gap-1 pr-1">
                    {ROLE_LABELS[m.role] ?? m.role} · {scopeName(m.scopeType, m.scopeId)}
                    <button
                      type="button"
                      aria-label={`Quitar ${m.role}`}
                      className="rounded-sm p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => {
                        void act(() =>
                          api(`/api/admin/users/${u.id}/memberships/${m.id}`, {
                            method: 'DELETE',
                          }),
                        ).catch(() => undefined);
                      }}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ),
          },
          {
            header: '2FA',
            cell: (u) =>
              u.totpEnabled ? (
                <StatusBadge label="Activo" tone="success" icon={ShieldCheck} />
              ) : (
                <StatusBadge label="Sin 2FA" tone="warning" icon={ShieldOff} />
              ),
          },
          {
            header: 'Estado',
            cell: (u) =>
              u.status === 'active' ? (
                <StatusBadge label="Activo" tone="success" icon={CheckCircle2} />
              ) : (
                <StatusBadge label="Deshabilitado" tone="destructive" icon={CircleSlash} />
              ),
          },
          {
            header: 'Último acceso',
            cell: (u) => (u.lastLoginAt ? dateTime(u.lastLoginAt) : 'Nunca'),
          },
          {
            header: '',
            className: 'w-40 text-right',
            cell: (u) => (
              <UserActions
                user={u}
                clients={clients.data ?? []}
                tenants={tenants.data ?? []}
                act={act}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

// ------------------------------------------------------------------ Planes

/** Campos numéricos por límite (en vez de un JSON): vacío = sin definir, "Sin límite" = ilimitado. */
export function LimitFields({
  value,
  allowUnlimited = true,
}: {
  value?: Limits | Record<string, number | null | undefined>;
  allowUnlimited?: boolean;
}) {
  const base = useId();
  const v = (value ?? {}) as Limits;
  const [unl, setUnl] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(LIMIT_KEYS.map((k) => [k, v[k] === null])),
  );
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-medium">Límites</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {LIMIT_KEYS.map((k) => {
          const def = LIMIT_DEFS[k];
          const cur = v[k];
          const id = `${base}-${k}`;
          const initial =
            cur === null || cur === undefined ? '' : String(def.cents ? cur / 100 : cur);
          return (
            <div key={k} className="flex flex-col gap-1.5 rounded-lg border bg-muted/30 p-2.5">
              <Label htmlFor={id} className="flex items-center gap-2 text-xs">
                <def.icon className="size-3.5 text-highlight" aria-hidden />
                {def.field}
                {def.unit ? (
                  <span className="font-normal text-muted-foreground">({def.unit})</span>
                ) : null}
              </Label>
              <input
                id={id}
                name={`limit_${k}`}
                type="number"
                min={0}
                step={def.cents ? '0.01' : '1'}
                inputMode="decimal"
                defaultValue={initial}
                placeholder="Sin definir"
                disabled={unl[k]}
                className="h-8 w-full rounded-md border border-input bg-card px-2 text-sm tabular-nums shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              />
              {allowUnlimited ? (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    name={`unl_${k}`}
                    checked={unl[k] ?? false}
                    onChange={(e) => {
                      setUnl({ ...unl, [k]: e.target.checked });
                    }}
                    className="accent-highlight"
                  />
                  Sin límite
                </label>
              ) : null}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

function FeatureFields({ value }: { value?: Record<string, boolean> }) {
  const keys = [...new Set([...Object.keys(FEATURE_LABELS), ...Object.keys(value ?? {})])];
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-medium">Funciones incluidas</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {keys.map((k) => (
          <label
            key={k}
            className="flex items-center gap-2 rounded-lg border bg-muted/30 px-2.5 py-2 text-sm"
          >
            <input
              type="checkbox"
              name={`feat_${k}`}
              defaultChecked={value?.[k] ?? false}
              className="accent-highlight"
            />
            {featureLabel(k)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function readFeatures(f: FormData, current?: Record<string, boolean>): Record<string, boolean> {
  const keys = [...new Set([...Object.keys(FEATURE_LABELS), ...Object.keys(current ?? {})])];
  return Object.fromEntries(keys.map((k) => [k, f.get(`feat_${k}`) === 'on']));
}

const PLAN_TONES: NonNullable<IconBadgeProps['tone']>[] = ['cyan', 'green', 'violet', 'orange'];

function PlanCard({
  plan,
  tone,
  onEdit,
  onArchive,
}: {
  plan: Plan;
  tone: NonNullable<IconBadgeProps['tone']>;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const lines = limitLines(plan.limits);
  const features = Object.entries(plan.features);
  const archived = plan.archivedAt !== null;
  return (
    <Card
      className={cn(
        'flex flex-col overflow-hidden transition-[box-shadow,transform] hover:shadow-raised',
        archived && 'border-dashed bg-muted/30 shadow-none',
      )}
    >
      <div className="flex items-start justify-between gap-3 p-5 pb-4">
        <div className="flex items-center gap-3">
          <IconBadge tone={archived ? 'neutral' : tone} size="lg">
            <Layers />
          </IconBadge>
          <div className="min-w-0">
            <h3 className="flex flex-wrap items-center gap-2 font-display text-lg leading-tight font-semibold">
              {plan.name}
              <Badge variant="secondary" className="px-1.5 py-0 font-sans text-[0.7rem]">
                v{plan.version}
              </Badge>
              {archived ? (
                <Badge variant="outline" className="py-0 font-sans text-[0.7rem]">
                  Archivado
                </Badge>
              ) : null}
            </h3>
            <code className="text-xs text-muted-foreground">{plan.code}</code>
          </div>
        </div>
      </div>
      <p className="px-5 pb-4">
        <span className="font-display text-3xl font-semibold tracking-tight tabular-nums">
          {price(plan.priceCents, plan.currency)}
        </span>{' '}
        <span className="text-sm text-muted-foreground">
          / {plan.interval === 'month' ? 'mes' : 'año'}
        </span>
      </p>
      <div className="flex flex-1 flex-col gap-4 border-t bg-muted/25 px-5 py-4">
        {lines.length ? (
          <ul className="flex flex-col gap-2 text-sm" aria-label={`Límites de ${plan.name}`}>
            {lines.map((l) => (
              <li key={l.key} className="flex items-center gap-2.5">
                <l.icon className="size-4 shrink-0 text-highlight" aria-hidden />
                <span className={cn(l.unlimited && 'font-medium')}>{l.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Sin límites definidos.</p>
        )}
        {features.length ? (
          <ul
            className="flex flex-col gap-1.5 border-t border-dashed pt-3 text-sm"
            aria-label={`Funciones de ${plan.name}`}
          >
            {features.map(([k, on]) => (
              <li
                key={k}
                className={cn('flex items-center gap-2.5', !on && 'text-muted-foreground')}
              >
                {on ? (
                  <Check className="size-4 shrink-0 text-success" aria-label="Incluida" />
                ) : (
                  <X className="size-4 shrink-0" aria-label="No incluida" />
                )}
                <span className={cn(!on && 'line-through decoration-muted-foreground/40')}>
                  {featureLabel(k)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-2 border-t px-5 py-3">
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil /> Editar plan
        </Button>
        <Button variant="ghost" size="sm" onClick={onArchive}>
          {archived ? <ArchiveRestore /> : <Archive />}
          {archived ? 'Restaurar' : 'Archivar'}
        </Button>
      </div>
    </Card>
  );
}

export function PlansSection() {
  const plans = useAdminList<Plan>('plans', '/api/admin/plans');
  const run = useAdminMutation(['plans'], 'Plan guardado');
  const [editing, setEditing] = useState<Plan | null>(null);
  const [archiving, setArchiving] = useState<Plan | null>(null);
  const planBody = (f: FormData, current?: Plan) => ({
    name: fd(f, 'name'),
    priceCents: Math.round(Number(fd(f, 'price')) * 100),
    currency: fd(f, 'currency'),
    interval: fd(f, 'interval'),
    limits: readLimits(f),
    features: readFeatures(f, current?.features),
  });
  const fields = (p?: Plan) => (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nombre" name="name" defaultValue={p?.name} required />
        <Field
          label="Precio"
          name="price"
          type="number"
          step="0.01"
          min="0"
          defaultValue={p ? p.priceCents / 100 : undefined}
          required
        />
        <NativeSelect
          label="Moneda"
          name="currency"
          defaultValue={p?.currency ?? 'EUR'}
          options={['EUR', 'USD', 'ARS'].map((c) => ({ value: c, label: c }))}
        />
        <NativeSelect
          label="Período"
          name="interval"
          defaultValue={p?.interval ?? 'month'}
          options={[
            { value: 'month', label: 'Mensual' },
            { value: 'year', label: 'Anual' },
          ]}
        />
      </div>
      <LimitFields value={p?.limits ?? { max_companies: 1, emails_sent_per_month: 1000 }} />
      <FeatureFields value={p?.features ?? { inbound_bot: false }} />
    </>
  );
  const sorted = [...(plans.data ?? [])].sort(
    (a, b) =>
      Number(a.archivedAt !== null) - Number(b.archivedAt !== null) || a.priceCents - b.priceCents,
  );
  return (
    <div className="flex flex-col gap-4">
      <SectionBar
        icon={Layers}
        title="Planes"
        description="Precio y límites de cada plan. Cambiarlos sube la versión; las suscripciones conservan la suya."
        action={
          <FormDialog
            title="Nuevo plan"
            wide
            trigger={
              <Button>
                <Plus /> Nuevo plan
              </Button>
            }
            onSubmit={(f) =>
              run(() =>
                api('/api/admin/plans', {
                  method: 'POST',
                  body: { ...planBody(f), code: fd(f, 'code') },
                }),
              )
            }
          >
            <Field
              label="Código"
              name="code"
              pattern="[a-z0-9][a-z0-9-]*"
              hint="minúsculas, números y guiones"
              required
            />
            {fields()}
          </FormDialog>
        }
      />
      {!plans.data ? (
        <div
          aria-busy="true"
          aria-label="Cargando"
          className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
        >
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-96 rounded-xl" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState icon={<Layers />} title="Todavía no hay planes" />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sorted.map((p, i) => (
            <li key={p.id}>
              <PlanCard
                plan={p}
                tone={PLAN_TONES[i % PLAN_TONES.length] ?? 'cyan'}
                onEdit={() => {
                  setEditing(p);
                }}
                onArchive={() => {
                  if (p.archivedAt) {
                    void run(() =>
                      api(`/api/admin/plans/${p.id}`, {
                        method: 'PATCH',
                        body: { archived: false },
                      }),
                    ).catch((err: unknown) => toast.error(errorMessage(err)));
                  } else setArchiving(p);
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <FormDialog
          key={editing.id}
          title={`Editar ${editing.name}`}
          description="Si cambias el precio o los límites, el plan pasa a una versión nueva. Las suscripciones actuales mantienen la suya."
          wide
          open
          onOpenChange={(v) => {
            if (!v) setEditing(null);
          }}
          onSubmit={(f) =>
            run(() =>
              api(`/api/admin/plans/${editing.id}`, {
                method: 'PATCH',
                body: planBody(f, editing),
              }),
            )
          }
        >
          {fields(editing)}
        </FormDialog>
      ) : null}
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(v) => {
          if (!v) setArchiving(null);
        }}
        title={`¿Archivar el plan ${archiving?.name ?? ''}?`}
        description="Deja de ofrecerse para suscripciones nuevas. Las que ya lo tienen siguen igual."
        confirmLabel="Archivar"
        destructive={false}
        onConfirm={() =>
          archiving
            ? run(() =>
                api(`/api/admin/plans/${archiving.id}`, {
                  method: 'PATCH',
                  body: { archived: true },
                }),
              )
            : Promise.resolve()
        }
      />
    </div>
  );
}

// ------------------------------------------------------------------ Suscripciones

/**
 * `renderActions`: acciones por suscripción (máquina de estados, add-ons, facturas). Se inyecta
 * desde el router para no crear un ciclo de imports con superadmin-billing.
 */
export function SubscriptionsSection({
  renderActions,
}: { renderActions?: (subscriptionId: string) => ReactNode } = {}) {
  const subs = useAdminList<Subscription>('subscriptions', '/api/admin/subscriptions');
  const details = useAdminList<SubscriptionDetail>(
    'subscription-details',
    '/api/admin/subscriptions/details',
  );
  const clients = useAdminList<Client>('clients', '/api/admin/clients');
  const plans = useAdminList<Plan>('plans', '/api/admin/plans');
  const run = useAdminMutation(['subscriptions'], 'Suscripción guardada');
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-col gap-4">
      <SectionBar
        icon={Repeat}
        title="Suscripciones"
        description="Qué plan tiene cada cliente, su período actual y su estado de cobro."
        action={
          <FormDialog
            title="Nueva suscripción (cobro manual)"
            trigger={
              <Button>
                <Plus /> Nueva suscripción
              </Button>
            }
            onSubmit={(f) => {
              const custom = fd(f, 'custom');
              return run(() =>
                api('/api/admin/subscriptions', {
                  method: 'POST',
                  body: {
                    clientId: fd(f, 'clientId'),
                    planId: fd(f, 'planId'),
                    status: fd(f, 'status'),
                    currentPeriodStart: fd(f, 'start'),
                    currentPeriodEnd: fd(f, 'end'),
                    customPriceCents: custom ? Math.round(Number(custom) * 100) : null,
                    notes: fd(f, 'notes') || null,
                  },
                }),
              );
            }}
          >
            <NativeSelect
              label="Cliente"
              name="clientId"
              options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <NativeSelect
              label="Plan"
              name="planId"
              options={(plans.data ?? [])
                .filter((p) => !p.archivedAt)
                .map((p) => ({
                  value: p.id,
                  label: `${p.name} (${money(p.priceCents, p.currency)})`,
                }))}
            />
            <NativeSelect
              label="Estado"
              name="status"
              defaultValue="active"
              options={SUBSCRIPTION_STATUSES.map((s) => ({
                value: s,
                label: SUB_STATUS[s]?.label ?? s,
              }))}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Inicio del período"
                name="start"
                type="date"
                defaultValue={today}
                required
              />
              <Field label="Fin del período" name="end" type="date" required />
            </div>
            <Field
              label="Precio pactado (opcional)"
              name="custom"
              type="number"
              step="0.01"
              min="0"
              hint="Vacío = el precio de lista del plan"
            />
            <Field label="Notas" name="notes" />
          </FormDialog>
        }
      />
      <DataTable
        rows={subs.data}
        empty="Sin suscripciones."
        emptyIcon={<Repeat />}
        columns={[
          {
            header: 'Cliente',
            cell: (s) => {
              const c = clients.data?.find((x) => x.id === s.clientId);
              return <Who name={c?.name ?? '—'} {...(c ? { colorKey: c.slug } : {})} />;
            },
          },
          {
            header: 'Plan',
            cell: (s) => {
              const plan = plans.data?.find((p) => p.id === s.planId);
              const det = details.data?.find((d) => d.id === s.id);
              const scheduled = det?.scheduledPlanId
                ? plans.data?.find((p) => p.id === det.scheduledPlanId)
                : undefined;
              return (
                <div className="flex flex-col gap-1">
                  <span className="inline-flex items-center gap-2 font-medium">
                    {plan?.name ?? '—'}
                    <Badge variant="secondary" className="px-1.5 py-0 text-[0.7rem]">
                      v{s.planVersion}
                    </Badge>
                  </span>
                  {scheduled || det?.addons.some((a) => !a.endedAt) ? (
                    <span className="flex flex-wrap gap-1">
                      {scheduled ? (
                        <Badge variant="outline" className="py-0 text-[0.7rem]">
                          <Repeat aria-hidden /> Pasa a {scheduled.name} el próximo período
                        </Badge>
                      ) : null}
                      {det?.addons
                        .filter((a) => !a.endedAt)
                        .map((a) => (
                          <Badge key={a.id} variant="outline" className="py-0 text-[0.7rem]">
                            <Plus aria-hidden /> {a.addonName} × {a.quantity}
                          </Badge>
                        ))}
                    </span>
                  ) : null}
                </div>
              );
            },
          },
          {
            header: 'Período',
            cell: (s) => (
              <span className="whitespace-nowrap">
                {fmtPeriod(s.currentPeriodStart, s.currentPeriodEnd)}
              </span>
            ),
          },
          {
            header: 'Precio',
            cell: (s) => {
              const plan = plans.data?.find((p) => p.id === s.planId);
              const per = plan?.interval === 'year' ? '/año' : '/mes';
              const currency = plan?.currency ?? 'EUR';
              if (s.customPriceCents !== null) {
                return (
                  <span className="flex flex-col">
                    <span className="font-medium whitespace-nowrap tabular-nums">
                      {price(s.customPriceCents, currency)} {per}
                    </span>
                    <span className="text-xs text-highlight">precio pactado</span>
                  </span>
                );
              }
              return plan ? (
                <span className="flex flex-col">
                  <span className="font-medium whitespace-nowrap tabular-nums">
                    {price(plan.priceCents, currency)} {per}
                  </span>
                  <span className="text-xs text-muted-foreground">precio de lista</span>
                </span>
              ) : (
                '—'
              );
            },
          },
          { header: 'Estado', cell: (s) => <SubStatusBadge status={s.status} /> },
          {
            header: '',
            className: 'w-56 text-right',
            cell: (s) =>
              renderActions ? (
                renderActions(s.id)
              ) : (
                <select
                  aria-label="Estado de la suscripción"
                  value={s.status}
                  onChange={(e) => {
                    const status = e.target.value;
                    void run(() =>
                      api(`/api/admin/subscriptions/${s.id}`, {
                        method: 'PATCH',
                        body: { status },
                      }),
                    ).catch((err: unknown) => toast.error(errorMessage(err)));
                  }}
                  className="h-8 rounded-md border border-input bg-card px-2 text-xs"
                >
                  {SUBSCRIPTION_STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {SUB_STATUS[st]?.label ?? st}
                    </option>
                  ))}
                </select>
              ),
          },
        ]}
      />
    </div>
  );
}

// ------------------------------------------------------------------ Pagos

const METHOD_LABEL: Record<(typeof PAYMENT_METHODS)[number], string> = {
  bank_transfer: 'Transferencia',
  cash: 'Efectivo',
  mercadopago: 'Mercado Pago',
  stripe: 'Stripe',
  other: 'Otro',
};

export const METHOD_ICON: Record<(typeof PAYMENT_METHODS)[number], LucideIcon> = {
  bank_transfer: Landmark,
  cash: Banknote,
  mercadopago: Wallet,
  stripe: CreditCard,
  other: Receipt,
};

export function PaymentsSection() {
  const payments = useAdminList<Payment>('payments', '/api/admin/payments');
  const clients = useAdminList<Client>('clients', '/api/admin/clients');
  const run = useAdminMutation(['payments'], 'Pago registrado');
  return (
    <div className="flex flex-col gap-4">
      <SectionBar
        icon={Wallet}
        title="Pagos"
        action={
          <FormDialog
            title="Registrar pago manual"
            trigger={
              <Button>
                <Plus /> Registrar pago
              </Button>
            }
            onSubmit={(f) =>
              run(() =>
                api('/api/admin/payments', {
                  method: 'POST',
                  body: {
                    clientId: fd(f, 'clientId'),
                    invoiceId: fd(f, 'invoiceId') || null,
                    amountCents: Math.round(Number(fd(f, 'amount')) * 100),
                    currency: fd(f, 'currency'),
                    method: fd(f, 'method'),
                    reference: fd(f, 'reference') || null,
                    receivedAt: fd(f, 'receivedAt'),
                  },
                }),
              )
            }
          >
            <NativeSelect
              label="Cliente"
              name="clientId"
              options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <Field label="Monto" name="amount" type="number" step="0.01" min="0.01" required />
            <NativeSelect
              label="Moneda"
              name="currency"
              options={['EUR', 'USD', 'ARS'].map((c) => ({ value: c, label: c }))}
            />
            <NativeSelect
              label="Método"
              name="method"
              options={PAYMENT_METHODS.map((m) => ({ value: m, label: METHOD_LABEL[m] }))}
            />
            <Field label="Referencia (nº de transferencia)" name="reference" />
            <Field
              label="Fecha de cobro"
              name="receivedAt"
              type="date"
              defaultValue={new Date().toISOString().slice(0, 10)}
              required
            />
            <Field label="Factura (id, opcional)" name="invoiceId" />
          </FormDialog>
        }
      />
      <DataTable
        rows={payments.data}
        empty="Sin pagos registrados."
        emptyIcon={<Wallet />}
        columns={[
          { header: 'Fecha', cell: (p) => fmtDate(p.receivedAt) },
          {
            header: 'Cliente',
            cell: (p) => {
              const c = clients.data?.find((x) => x.id === p.clientId);
              return <Who name={c?.name ?? '—'} {...(c ? { colorKey: c.slug } : {})} />;
            },
          },
          {
            header: 'Monto',
            className: 'text-right',
            cell: (p) => (
              <span className="font-medium tabular-nums">{money(p.amountCents, p.currency)}</span>
            ),
          },
          {
            header: 'Método',
            cell: (p) => {
              const Icon = METHOD_ICON[p.method];
              return (
                <span className="inline-flex items-center gap-2">
                  <Icon className="size-4 text-muted-foreground" aria-hidden />
                  {METHOD_LABEL[p.method]}
                </span>
              );
            },
          },
          { header: 'Referencia', cell: (p) => p.reference ?? '—' },
        ]}
      />
    </div>
  );
}

// ------------------------------------------------------------------ Auditoría

export function AuditSection() {
  const audit = useAdminList<AuditEntry>('audit', '/api/admin/audit?limit=100');
  return (
    <div className="flex flex-col gap-4">
      <SectionBar
        icon={ScrollText}
        title="Auditoría"
        description="Los últimos 100 cambios hechos en la plataforma, con quién y desde dónde."
      />
      <DataTable
        rows={audit.data}
        empty="Sin eventos."
        emptyIcon={<ScrollText />}
        columns={[
          {
            header: 'Cuándo',
            cell: (a) => <span className="whitespace-nowrap">{dateTime(a.at)}</span>,
          },
          {
            header: 'Acción',
            cell: (a) => (
              <code className="rounded-md bg-muted px-1.5 py-0.5 text-xs">{a.action}</code>
            ),
          },
          {
            header: 'Entidad',
            cell: (a) => `${a.entity ?? '—'}${a.entityId ? ` · ${a.entityId.slice(0, 8)}` : ''}`,
          },
          {
            header: 'Actor',
            cell: (a) => (a.actorUserId ? a.actorUserId.slice(0, 8) : 'sistema'),
          },
          { header: 'IP', cell: (a) => a.ip ?? '—' },
        ]}
      />
    </div>
  );
}
